<template>
  <div>
    <portal-breadcrumbs v-if="!showInModal">
      <span>
        <span class="aLink mr-1" @click="$router.push('/redirect/settings')">
          <i class="fas fa-cogs"></i>
          {{ t("sidebar.settings.title") }}</span
        >/
        <span>{{ t("views.SettingsEntry.headlines.main") }}</span>
        /

        <span class="aLink" @click="$router.push('/costCenters')">{{
          t("views.ih.CostCenter.costCenters")
        }}</span>
        /
        <strong>{{ t("views.ih.CostCenter.updateCostCenter") }} </strong>
      </span>
    </portal-breadcrumbs>
    <InputFrame
      :title="t('views.ih.CostCenter.createNewCostCenter')"
      :loading="loading"
      :display-mode="showInModal ? InputFrameMode.modal : undefined"
      create
      :submit="
        async () => {
          await createCostCenter();
          return { success: true };
        }
      "
      @cancel="cancel"
    >
      <InputRow
        v-if="props.siteId == null"
        :model-value="siteIdRef"
        :display-name="t('global.site')"
        name="site"
        required
      >
        <template #default="{ field: inputField }">
          <SearchSelect
            v-bind="inputField"
            v-model="siteIdRef"
            :mode="SelectMode.SITE"
            :required="true"
            class="w-100"
          />
        </template>
      </InputRow>
      <CCComponent :cost-center="costCenter" :site-id="siteIdRef" />

      <template #frame-ok>
        <i class="fas fa-plus mr-2" />{{ t("views.ih.CostCenter.createNewCostCenter") }}
      </template>
    </InputFrame>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, type PropType } from "vue";
import { useRouter } from "vue-router";
import { useOGCostCenterCreateMutation, type OGCostCenterCreateMutation } from "@/graphql";
import CCComponent from "../components/CCComponent.vue";
import InputFrame from "@/components/shared/form/InputFrame.vue";
import type { CostCenterEditType } from "../helper/CR_TP_Type";
import usePropsAsDefault from "@/functions/usePropsAsDefault";
import { InputFrameMode } from "@/components/shared/form/InputFrameMode";
import InputRow from "@/components/shared/form/InputRow.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";

const props = defineProps({
  showInModal: {
    type: Boolean,
    required: false,
    default: false,
  },
  defaultCostCenter: {
    type: Object as PropType<{ name: string }>,
    required: false,
    default: undefined,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const siteIdRef = usePropsAsDefault({
  dynamicProps: computed(() => props.siteId),
  replace: true,
});

const emit = defineEmits<{
  created: [costCeter: OGCostCenterCreateMutation["OGCostCenterCreate"]];
  cancel: [];
}>();
const router = useRouter();

function getDefaultCostCenter(predefined?: Partial<CostCenterEditType>): CostCenterEditType {
  return {
    ...(predefined ?? {}),
    name: "",
    description: "",
    path: "",
    code: "",
    parentId: undefined,
  };
}

const costCenter = usePropsAsDefault({
  dynamicProps: computed(() => getDefaultCostCenter(props.defaultCostCenter)),
  replace: true,
});

const { mutate: createCostCenterMutation, loading } = useOGCostCenterCreateMutation({});

async function createCostCenter() {
  const result = await createCostCenterMutation(
    {
      costCenter: {
        name: costCenter.value.name,
        description: costCenter.value.description,
        code: costCenter.value.code,
        parentId: costCenter.value.parentId,
      },
    },
    {
      refetchQueries: ["CostCentersForSelect"],
    },
  );

  if (props.showInModal) {
    if (result?.data?.OGCostCenterCreate != null) {
      emit("created", result?.data?.OGCostCenterCreate);
    }
  } else {
    router.back();
  }
}

function cancel() {
  if (!props.showInModal) {
    router.back();
  } else {
    emit("cancel");
  }
}

const { t } = useTranslation();
</script>
